<template>
      <div>
        <!-- setting to not show this button, bulk approvals looks working but we decided to take it out for now -->
        <ActionButton
          v-if="false"
          button-text="APPROVE"
          aria-label="Approve all selected items"
          :default-class="'flex !justify-start text-teal-700 p-4 w-full font-semibold rounded'"
          :loading-class="'flex !justify-start text-teal-700 p-4 w-full font-semibold rounded opacity-50 cursor-not-allowed'"
          :on-click-action="approveAllSelected"
          data-test-id="approve-all-button"
          :disabled="selectedApplications.length === 0"
        />
      </div>
      <data-table
        v-model:selection="selectedApplications"
        :value="transformItems()"
        paginator
        :loading="!isApprovalLoaded"
        :rows="25"
        stripedRows
        selectionMode="multiple"
        dataKey="application_id"
        tableStyle="w-full; border-spacing: 0 !important"
        :pt="myStudentsDataTableThemeOverride"
      >
        <template #empty>{{ noResultsText }}</template>
        <template #loading>
          <div class="bg-gray-300 w-full h-10">
            <SkeletonLoader class="rounded-md w-full h-10" />
          </div>
        </template>
        <!-- <column selectionMode="multiple" headerStyle="width: 3rem;" /> -->
        <column field="student.first_name_legal" header="First Name">
          <template #body="{ data }">
            <text-cell
              :value="
                data.student.first_name_legal ||
                data.student.first_name_preferred
              "
            />
          </template>
        </column>
        <column field="student.last_name" header="Last Name">
          <template #body="{ data }"
            ><text-cell :value="data.student.last_name" />
          </template>
        </column>
        <column field="student.email" header="Email">
          <template #body="{ data }"
            ><text-cell
              :value="data.student.email"
              custom-class="text-teal-900"
          /></template>
        </column>
        <column header="Program Session Name">
          <template #body="{ data }"
            ><text-cell
              :value="
                data?.order?.session?.name ?? ''
              "
              custom-class="text-teal-900"
          /></template>
        </column>
        <column header="Program Dates">
          <template #body="{ data }"
            ><text-cell
              :value="
                startAndEndDate(
                  data.order.session.academic_start_date,
                  data.order.session.academic_end_date
                )
              "
          /></template>
        </column>
        <column
          field="data.order.session.deadline"
          header="Application Deadline"
        >
          <template #body="{ data }"
            ><text-cell :value="formatDate(data.order.session.deadline)" />
          </template>
        </column>
        <column field="order.core.created_at" header="Applied Date">
          <template #body="{ data }"
            ><text-cell :value="formatDate(data.order.core.created_at)" />
          </template>
        </column>
        <column
          header="Action"
          :pt="{ headercontent: 'flex items-center justify-center' }"
        >
          <template #body="{ data }"
            ><action-cell
              v-if="isApprovalLoaded"
              :show-action-button="
                data?.order?.approval_status === approvalStatus.APPROVE
              "
              :data="data"
              @clicked-event="approveApplication"
          /></template>
        </column>
      </data-table>
</template>
<script setup>
import { format, parse } from "date-fns";
import { cloneDeep, isEqual } from "lodash";
import Column from "primevue/column";
import { usePrimeVue } from "primevue/config";
import DataTable from "primevue/datatable";
import { computed, ref, watch } from "vue";
import { useRoute } from "vue-router";
import { useStore } from "vuex";
import ActionButton from "@/components/MyStudentDetail/ActionButton.vue";
import { approvalStatus } from "@/components/MyStudents/approvalStatus.js";
import ActionCell from "@/components/MyStudents/DataCells/ActionCell.vue";
import SkeletonLoader from "@/components/shared/loaders/BaseSkeletonLoader.vue";
import { CERTIFY_TYPE, ORDER_STATUS } from "@/constants.js";
import formService from "@/services/form";
import TextCell from "./DataCells/TextCell.vue";
import { myStudentsDataTableTheme } from "./primeVueTheme";

const route = useRoute();
const store = useStore();
const emit = defineEmits(["update-application-approvals"]);

const props = defineProps({
  orders: {
    type: Array,
    default: [],
  },
  selectedStatus: {
    type: String,
  },
});
const { config } = usePrimeVue();
const selectedApplications = ref([]);
const applicationIds = ref(undefined);
const submissions = ref(undefined);
const isApprovalLoaded = ref(false);

const startAndEndDate = (startDate, endDate) => {
  if (startDate && endDate) {
    return formatDate(startDate) + " - " + formatDate(endDate);
  }
  return "";
};

const formatDate = (date) => {
  if (date) return format(parse(date, "yyyy-MM-dd", new Date()), "MM/dd/yyyy");
  return "";
};

const noResultsText = computed(() => (isApprovalLoaded.value ? "No Results Found." : ""));

// Transform the items per student to be per order it will show in the list
const transformItems = () => {
  const transformedItems = [];
  const localApplicationIds = new Set();
  // filter out orders as Draft and WithDrawn as well as Custom
  props.orders.forEach((orderItem) => {
    const order = orderItem.order;
    if (
      order?.core?.application_id &&
      !transformedItems.find((each) => each.order?.core?.application_id === order?.core?.application_id) &&
      ![ORDER_STATUS.draft, ORDER_STATUS.withdrawn].includes(order?.core?.status) &&
      order?.session?.channel !== "Custom"
    ) {
      const itemWithNoOrder = cloneDeep(orderItem);
      transformedItems.push({
        ...itemWithNoOrder,
        application_id: order?.core?.application_id, // just adding the unique element here to allow us to select them individually
        student: { ...orderItem.student },
        order: { ...order, approval_status: null },
      });

      if (order?.core?.application_id) {
        localApplicationIds.add(order.core.application_id);
      }
    }
  });
  if (!isEqual(applicationIds.value, localApplicationIds)) applicationIds.value = localApplicationIds;
  return filteredItems(transformedItems);
};

const addStatusToItems = (items) => {
  return items.map((item) => {
    let status = null;
    if (submissions.value?.nonApprovedApplicationIds?.includes(item.order?.core?.application_id))
      status = approvalStatus.APPROVE;
    if (submissions.value?.approvedApplicationIds?.includes(item.order?.core?.application_id))
      status = approvalStatus.COMPLETED;
    item.order.approval_status = status;
    return item;
  });
};

const applyFilters = (items) => {
  if (props.selectedStatus === approvalStatus.APPROVE) {
    return items?.filter((each) =>
      submissions.value?.nonApprovedApplicationIds?.includes(each.order?.core?.application_id),
    );
  }
  if (props.selectedStatus === approvalStatus.COMPLETED) {
    return items?.filter((each) =>
      submissions.value?.approvedApplicationIds?.includes(each.order?.core?.application_id),
    );
  }
  return items;
};

const filteredItems = (items) => {
  const filtersAppliedItems = addStatusToItems(items);
  return applyFilters(filtersAppliedItems);
};

const getFormioApplications = (applicationIds) => {
  if (applicationIds.length === 0) {
    // Sending a response as empty to keep total cards up to date
    const applicationsApprovals = {
      approvedApplicationIds: [],
      nonApprovedApplicationIds: [],
    };
    emit("update-application-approvals", applicationsApprovals);
    return;
  }
  return formService
    .getApplicationsSubmission({
      applicationIds: applicationIds,
    })
    .then((response) => {
      const applicationsApprovals = {
        approvedApplicationIds: response.approved_application_ids,
        nonApprovedApplicationIds: response.non_approved_application_ids,
      };
      submissions.value = applicationsApprovals;
      emit("update-application-approvals", applicationsApprovals);
    });
};

watch(applicationIds, (newApplicationIds, oldApplicationIds) => {
  isApprovalLoaded.value = false;
  if (!isEqual(newApplicationIds, oldApplicationIds)) getFormioApplications([...newApplicationIds]);
  isApprovalLoaded.value = true;
});

const approveAllSelected = async () => {
  const promises = [];
  selectedApplications.value.forEach((each) => {
    promises.push(approveApplication(each, false));
  });
  await Promise.allSettled(promises); // TODO maybe we can loop into failed promises to log something in Sentry
  selectedApplications.value = [];
  getFormioApplications([...applicationIds.value]);
};

const approveApplication = async (data, shouldGetSubmissions = false) => {
  const formUrl = "advisorapproval";
  const formioSubmissionExists = (formPath, applicationId) => {
    return formService
      .submissionExists(formPath, { "data.application_id": applicationId })
      .then((queryResult) => {
        return queryResult._id ? true : false;
      })
      .catch(() => false);
  };
  const submitApproval = async () => {
    const studentFullName =
      (data?.student?.first_name_preferred || data?.student?.first_name_legal) + " " + data?.student?.last_name;
    const advisorFullName = store.getters?.getCurrentUser?.firstName + " " + store.getters?.getCurrentUser?.lastName;
    try {
      const payload = {
        data: {
          application_id: data?.order?.core?.application_id,
          studentname: studentFullName,
          programname: data?.order?.session?.session_id,
          program: null,
          institutionname: null,
          institution: {
            value: store.getters?.getCurrentUser?.university?.value,
            label: store.getters?.getCurrentUser?.university?.label,
          },
          advisoremail: store.getters?.getCurrentUser?.email,
          advisorname: advisorFullName,
          certifytype: CERTIFY_TYPE.APPROVED,
        },
      };
      const submissionResponse = await formService.createSubmission(formUrl, payload).then((submission) => {
        return submission._id ? true : false;
      });

      return submissionResponse ? true : false;
    } catch (e) {
      return false;
    }
  };
  try {
    const submissionExists = await formioSubmissionExists(formUrl, data?.order?.core?.application_id);

    if (!submissionExists) {
      const approvalSent = await submitApproval();
      if (shouldGetSubmissions && approvalSent) getFormioApplications([...applicationIds.value]);
    }
  } catch (e) {
    throw new Error("Approve application process failed => ", e);
  }
};
const getHeaderCellClasses = (context, props) => {
  const headerCellConfig = myStudentsDataTableTheme.column.headercell({
    context,
    props,
  });
  return [...headerCellConfig.class, "border-gray-275", "text-university-primary"];
};
const myStudentsDataTableThemeOverride = {
  ...myStudentsDataTableTheme,
  column: {
    bodycell: (props) => {
      let base = "p-4 border-b border-gray-275";
      if (props.context.index === 1) {
        base = "p-4 border-b border-gray-275 text-teal-900 font-medium text-sm transition-colors duration-200";
      }
      return { class: base };
    },
    headercell: ({ context, props }) => ({
      class: getHeaderCellClasses(context, props),
    }),
    rowCheckbox: {
      input: {
        class: [
          ...(config.pt.datatable?.column?.rowCheckbox?.input?.class ?? []),
          // Colors
          "checked:bg-teal-700",
          "checked:border-teal-700",

          // Shape
          "!opacity-100",
        ],
      },
    },
  },
};
</script>
